import React, { useRef } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ScreeningDialog from 'components/ScreeningComposer/ScreeningDialog';

// The styled "leave this tool?" dialog, paired with useLeaveGuard.
//
// LeaveGuardDialog (the default export) guards leaving a whole tool. It keeps the class names
// of the tools' older copies of this dialog (dialog_box / modal-header-content /
// audit-add-modal-* / wizard-body-text / btn-new-clear / btn-new-automate) so every tool looks
// the same. Its save button is optional: a tool with no save mechanism shows two actions
// instead of a button that can't do anything.
//
// LeaveGuardConfirmDialog confirms one in-app action (e.g. a "Check tariffs" link) rather than
// leaving a whole tool. It is a ScreeningDialog panel, not Mui Dialog: App.tsx makes Dialog
// paper inherit a transparent background and paints DialogTitle as a page-colored pill.
// Cancel is a button, and "Don't ask again" is a checkbox the caller applies to whichever
// action is taken, using useLeaveGuard's opt-out storage.

interface DialogBase {
  open: boolean;
  /** e.g. "Leave Design Assistant?" */
  title: string;
  /** Explains what is lost. Should be honest about whether saving is possible. */
  body: React.ReactNode;
  onClose: () => void;
  onLeaveWithoutSaving: () => void;
}

interface LeaveDialogProps extends DialogBase {
  onLeaveAndOptOut: () => void;
  /** Omit when the tool has no save; the middle button is then not rendered at all. */
  onSave?: () => void;
  saveLabel?: string;
}

interface DontAskAgain {
  checked: boolean;
  onChange: (checked: boolean) => void;
}

interface ConfirmDialogProps extends DialogBase {
  /** The label for onLeaveWithoutSaving. With no `save` it is the primary action. */
  leaveLabel?: string;
  save?: { label: string; onClick: () => void };
  /** Renders a "Don't ask again" checkbox. */
  dontAskAgain?: DontAskAgain;
  /** A save is running. Actions are disabled and the dialog can't close. */
  busy?: boolean;
}

export const LeaveGuardConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  body,
  onClose,
  onLeaveWithoutSaving,
  leaveLabel = 'Leave without saving',
  save,
  dontAskAgain,
  busy = false,
}) => {
  const titleId = React.useId();
  const bodyId = React.useId();
  const primaryRef = useRef<HTMLButtonElement>(null);
  if (!open) {
    return null;
  }
  const dismiss = () => {
    if (!busy) {
      onClose();
    }
  };
  return (
    <ScreeningDialog
      labelledBy={titleId}
      panelClassName='psc-confirm'
      onClose={dismiss}
      initialFocus={primaryRef}
    >
      <div className='psc-confirm-head'>
        <h2 id={titleId} className='psc-confirm-title'>
          {title}
        </h2>
        <button
          type='button'
          className='psc-btn psc-btn--icon psc-btn--ghost'
          aria-label='Close'
          onClick={dismiss}
          disabled={busy}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>
      <p id={bodyId} className='psc-confirm-body'>
        {body}
      </p>
      {dontAskAgain && (
        <label className='psc-confirm-optout'>
          <input
            type='checkbox'
            checked={dontAskAgain.checked}
            onChange={(e) => dontAskAgain.onChange(e.target.checked)}
            disabled={busy}
          />
          Don&apos;t ask again
        </label>
      )}
      <div className='psc-confirm-actions'>
        {save ? (
          <>
            <button
              ref={primaryRef}
              type='button'
              className='psc-primary'
              onClick={save.onClick}
              disabled={busy}
            >
              {busy ? 'Saving…' : save.label}
            </button>
            <button
              type='button'
              className='psc-btn'
              onClick={onLeaveWithoutSaving}
              disabled={busy}
            >
              {leaveLabel}
            </button>
            <button
              type='button'
              className='psc-btn psc-btn--ghost'
              onClick={dismiss}
              disabled={busy}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              ref={primaryRef}
              type='button'
              className='psc-primary'
              onClick={onLeaveWithoutSaving}
              disabled={busy}
            >
              {leaveLabel}
            </button>
            <button
              type='button'
              className='psc-btn psc-btn--ghost'
              onClick={dismiss}
              disabled={busy}
            >
              Cancel
            </button>
          </>
        )}
      </div>
    </ScreeningDialog>
  );
};

const LeaveGuardDialog: React.FC<LeaveDialogProps> = ({
  open,
  title,
  body,
  onClose,
  onLeaveWithoutSaving,
  onLeaveAndOptOut,
  onSave,
  saveLabel = 'Save Ticket',
}) => (
  <Dialog
    className='dialog_box'
    open={open}
    onClose={onClose}
    maxWidth='sm'
    fullWidth
    PaperProps={{
      style: {
        backgroundColor: 'var(--bg-colors)',
        boxShadow:
          '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        borderRadius: '0.5rem',
        maxWidth: '620px',
        width: '90%',
        margin: '0 auto',
      },
    }}
  >
    <DialogTitle className='modal-header-content modal-title'>
      {title}
      <button
        type='button'
        className='audit-add-modal-head-cont-icon'
        aria-label='Close'
        onClick={onClose}
        style={{ cursor: 'pointer', boxSizing: 'content-box' }}
      >
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='18'
          height='18'
          viewBox='0 0 18 18'
          fill='none'
          aria-hidden='true'
        >
          <path
            d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
            stroke='white'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>
    </DialogTitle>
    <DialogContent className='dialog-content-base'>
      <div
        className='audit-add-modal-cont-body'
        style={{ paddingTop: '12px', paddingBottom: '12px' }}
      >
        <p className='wizard-body-text' style={{ marginBottom: '20px' }}>
          {body}
        </p>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <button className='btn-new-clear' onClick={onLeaveWithoutSaving}>
            <div>Leave Without Saving</div>
          </button>
          {onSave && (
            <button className='btn-new-automate' onClick={onSave}>
              <div>{saveLabel}</div>
            </button>
          )}
          <button className='btn-new-automate' onClick={onLeaveAndOptOut}>
            <div>Leave + Don&apos;t Show Again</div>
          </button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default LeaveGuardDialog;
